
import UserService from '../../services/UserService';
import ErrorHandlerHelpers from '../../mixins/ErrorHandlerHelpers';

// TYPES
const SET_USER = 'SET_USER';
const CLEAR_USER = 'CLEAR_USER';
const SET_USER_DETAILS = 'SET_USER_DETAILS';
const SET_NAVBAR_ENV_DISP_TEXT = 'SET_NAVBAR_ENV_DISP_TEXT';
const SET_SHOW_OFFLINE_DEV_TOOLS = 'SET_SHOW_OFFLINE_DEV_TOOLS';
const SET_AAD_ENABLED = 'SET_AAD_ENABLED';
const SET_ACTIVE_SITE = 'SET_ACTIVE_SITE';
const SET_CONNECTED_TO_SERVER = 'SET_CONNECTED_TO_SERVER';
const SET_BUILD_VERSION = 'SET_BUILD_VERSION';
const SET_VOR_OVERVIEW_URL = 'SET_VOR_OVERVIEW_URL';

// STATE
const state = {
  user: null,
  activeSite: null,
  userDetails: null,
  navBarEnvironmentDisplayText: null,
  showOfflineDevTools: false,
  aadEnabled: false,
  isClientConnectedToServer: false,
  version: '',
  vorVesselOverviewBaseUrl: '',
};

// GETTERS
const getters = {
  isClientConnectedToServer: (state) => state.isClientConnectedToServer,
  isUserLoggedIn: (state) => state.user !== null,
  isUser: (state) => state.user
    && state.user.roles
    && state.user.roles.includes('User'),
  isAdmin: (state) => state.user
    && state.user.roles
    && state.user.roles.includes('Admin'),
  isDataAdmin: (state) => state.user
    && state.user.roles
    && state.user.roles.includes('DataAdmin'),
  isTankAdmin: (state) => state.user
    && state.user.roles
    && state.user.roles.includes('TankAdmin'),
  isReadOnlyUser: (state) => state.user
    && state.user.roles
    && state.user.roles.includes('ReadOnly')
    && !state.user.roles.includes('Admin')
    && !state.user.roles.includes('DataAdmin')
    && !state.user.roles.includes('User'),
  showOfflineDevTools: (state) => state.showOfflineDevTools,
  aadEnabled: (state) => state.aadEnabled,
  getUser: (state) => state.user,
  buildVersion: (state) => state.version,
  getVorVesselOverviewUrl: (state) => state.vorVesselOverviewBaseUrl,
  getActiveSite: (state) => {
    let activeSite = null;
    if (state.user) {
      const userSites = state.user.assignedSites;
      activeSite = state.activeSite || userSites[0];
    }
    return activeSite;
  },
  getActiveSiteId: (state) => {
    let activeSiteId = null;
    if (state.user) {
      const userSites = state.user.assignedSites;
      activeSiteId = (state.activeSite || userSites[0]).id;
    }
    return activeSiteId;
  },
  getActiveSiteConfig: (state) => state.user ? state.user.activeSiteConfig : {},
  nonActiveSites: (state) => {
    let nonActiveSites = [];
    if (state.user) {
      const userSites = state.user.assignedSites;
      const activeSite = state.activeSite || userSites[0];
      nonActiveSites = userSites.filter((x) => x.id !== activeSite.id);
    }
    return nonActiveSites;
  },
  getActiveSiteLocations: (state, getters) => {
    let locations = [];
    const activeSite = getters.getActiveSite;
    if (activeSite && activeSite.locations) {
      locations = activeSite.locations.map((x) => x.location);
    }
    return locations;
  },
};

// MUTATIONS
const mutations = {
  [SET_USER](state, user) {
    state.user = user;
  },
  [CLEAR_USER](state) {
    state.user = null;
  },
  [SET_USER_DETAILS](state, userDetails) {
    state.userDetails = userDetails;
  },
  [SET_NAVBAR_ENV_DISP_TEXT](state, navBarEnvironmentDisplayText) {
    state.navBarEnvironmentDisplayText = navBarEnvironmentDisplayText;
  },
  [SET_SHOW_OFFLINE_DEV_TOOLS](state, showOfflineDevTools) {
    state.showOfflineDevTools = showOfflineDevTools;
  },
  [SET_BUILD_VERSION](state, version) {
    state.version = version;
  },
  [SET_VOR_OVERVIEW_URL](state, url) {
    state.vorVesselOverviewBaseUrl = url;
  },
  [SET_AAD_ENABLED](state, aadEnabled) {
    state.aadEnabled = aadEnabled;
  },
  [SET_ACTIVE_SITE](state, site) {
    state.activeSite = site;
  },
  [SET_CONNECTED_TO_SERVER](state, isConnected) {
    state.isClientConnectedToServer = isConnected;
  },
};

// ACTIONS
const actions = ({
  login({ commit }, user) {
    return UserService.login(user.username, user.password)
      .then((x) => x.data)
      .then((userData) => {
        const token = userData.jwtBearerToken;
        localStorage.setItem('user', JSON.stringify(userData)); // Store user data in local storage as well so no need to relogin on page reload.
        localStorage.setItem('access_token', token);

        commit(SET_USER, userData);
        commit(SET_ACTIVE_SITE, userData.activeSite);
      })
      .catch(ErrorHandlerHelpers.handleServiceError);
  },
  loginWithAadCookie({ commit }) {
    UserService.loginWithAadCookie()
      .then((x) => x.data)
      .then((userData) => {
        const token = userData.jwtBearerToken;
        localStorage.setItem('access_token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        commit(SET_USER, userData);
      })
      .catch(ErrorHandlerHelpers.handleServiceError);
  },
  logout({ commit }) {
    return UserService.logout()
      .then((x) => x.data)
      .then((resp) => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        commit(CLEAR_USER);
      });
  },
  changePassword({ dispatch }, changePasswordPayload) {
    const { currentPassword, newPassword, newPasswordConfirm } = changePasswordPayload;
    return UserService.changePassword(currentPassword, newPassword, newPasswordConfirm)
      .catch(ErrorHandlerHelpers.handleServiceError);
  },
  reloadUserData({ commit }, activeSiteId) {
    return UserService.reloadUserData(state.user.username, activeSiteId)
      .then((x) => x.data)
      .then((userData) => {
        const token = userData.jwtBearerToken;
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('access_token', token);
        commit(SET_USER, userData);
        commit(SET_ACTIVE_SITE, userData.activeSite);
      })
      .catch(ErrorHandlerHelpers.handleServiceError);
  },
  checkUserValidity({ dispatch }) {
    return UserService.checkUserTokenIsValid()
      .catch((err) => {
        dispatch('logout');
      });
  },
  clearUser({ commit }) {
    commit(CLEAR_USER);
  },
  setUserFromLocalStorage({ commit, dispatch }, user) {
    commit(SET_USER, user);
  },
  setActiveSite({ commit, dispatch }, site) {
    commit(SET_ACTIVE_SITE, site);
    dispatch('reloadUserData', site ? site.id : null);
  },
  setIsClientConnectedToServer({ commit }, isConnected) {
    commit(SET_CONNECTED_TO_SERVER, isConnected);
  },
  getUserDetailsFromToken({ commit }) {
    UserService.getUserDetailsFromToken()
      .then((x) => x.data)
      .then((userDetailsStr) => {
        const userDetails = userDetailsStr;
        commit(SET_USER_DETAILS, userDetails);
      })
      .catch(ErrorHandlerHelpers.handleServiceError);
  },
  getNavbarEnvDisplayText({ commit }) {
    UserService.getUISettings()
      .then((x) => x.data)
      .then((data) => {
        const {
          showOfflineDevTools,
          navBarEnvironmentDisplayText,
          aadEnabled,
          version,
          vorVesselOverviewBaseUrl,
        } = data;
        commit(SET_NAVBAR_ENV_DISP_TEXT, navBarEnvironmentDisplayText);
        commit(SET_SHOW_OFFLINE_DEV_TOOLS, showOfflineDevTools);
        commit(SET_AAD_ENABLED, aadEnabled);
        commit(SET_BUILD_VERSION, version);
        commit(SET_VOR_OVERVIEW_URL, vorVesselOverviewBaseUrl);
      });
  },
});

export default {
  namespaced: true, // Name-space actions, getters and mutations (state always namespaced)
  state,
  mutations,
  actions,
  getters,
};
